import React, { useCallback } from 'react';
import { motion } from 'framer-motion';
import * as icons from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { CheckCircle2 } from 'lucide-react';

/**
 * Resolve a lucide-react icon by name string.
 * Falls back to a generic circle if the icon name isn't found.
 */
function getIcon(name) {
  return icons[name] || icons.Circle;
}

export default function TutorialModal({ step, onNext, onDismiss, onComplete, onRestart }) {
  const handleAction = useCallback((action) => {
    switch (action) {
      case 'next': return onNext?.();
      case 'dismiss': return onDismiss?.();
      case 'complete': return onComplete?.();
      case 'restart': return onRestart?.();
      default: return;
    }
  }, [onNext, onDismiss, onComplete, onRestart]);

  if (!step) return null;

  const { title, description, content, actions } = step;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onDismiss?.(); }}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="text-xl">{title}</DialogTitle>
          <DialogDescription className="text-sm mt-2">{description}</DialogDescription>
        </DialogHeader>

        <div className="py-2">
          {/* Feature list (welcome steps) */}
          {content?.features && (
            <ul className="space-y-3">
              {content.features.map((feature, i) => {
                const Icon = getIcon(feature.icon);
                return (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="flex items-center gap-3 text-sm text-foreground"
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <span>{feature.text}</span>
                  </motion.li>
                );
              })}
            </ul>
          )}

          {/* Bullet list (completion steps) */}
          {content?.bullets && (
            <ul className="space-y-2.5">
              {content.bullets.map((bullet, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-start gap-2.5 text-sm text-foreground"
                >
                  <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <span>{bullet}</span>
                </motion.li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          {actions?.secondary && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleAction(actions.secondary.action)}
            >
              {actions.secondary.label}
            </Button>
          )}
          {actions?.primary && (
            <Button
              size="sm"
              onClick={() => handleAction(actions.primary.action)}
            >
              {actions.primary.label}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
